import { useMemo } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { statsKeys } from '@/shared/lib/query/queryKeys';

import { fetchDashboardEntityCounts } from '@/features/home-stats/api/dashboardEntityCountsApi';
import type { DashboardEntityCountsResponse } from '@/features/home-stats/api/dashboardEntityCountsApi';
import {
  cycloneEntityCountsToChartData,
  fetchTotalDataAssets,
  mergeCycloneIntoOpenMetadataOverview,
  processDataAssetsOverview,
} from '@/features/home-stats/api/dataInsightsApi';
import type { DataAssetsOverview, DataInsightsResponse } from '@/features/home-stats/api/types';

const sharedQueryOptions = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false as const,
};

/**
 * OpenMetadata(총 데이터 자산 차트) + Cyclone `entity-counts` 를 **병렬** 요청하고,
 * `useSuspenseQueries`로 **둘 다 완료된 뒤에만** suspend 해제 → 한 번에 렌더링.
 *
 * 표시용 `overview`: OpenMetadata `total_data_assets` **전체**(테이블 등)를 쓰고,
 * Cyclone 은 `experiment` / `run` / `feature_set` / `dataset` 네 그룹만 일별 max 로 보정한다.
 * Cyclone 의 `run_*` 종속 행은 제외한다.
 */
export const useDataAssetsOverview = (period: 7 | 14 | 30 | 90 = 7) => {
  const [openMetadataQuery, cycloneEntityCountsQuery] = useSuspenseQueries({
    queries: [
      {
        queryKey: statsKeys.dataAssetsOverview(period),
        queryFn: (): Promise<DataInsightsResponse> => fetchTotalDataAssets(period),
        ...sharedQueryOptions,
      },
      {
        queryKey: statsKeys.dashboardEntityCounts(period),
        queryFn: (): Promise<DashboardEntityCountsResponse> => fetchDashboardEntityCounts(period),
        ...sharedQueryOptions,
      },
    ],
  });

  const overview: DataAssetsOverview = useMemo(() => {
    const today = dayjs().startOf('day');
    const openMetadataChart = openMetadataQuery.data;
    const cyclone = cycloneEntityCountsQuery.data;

    const openMetadataOverview = processDataAssetsOverview(openMetadataChart, period, today);
    const cycloneOverview = processDataAssetsOverview(cycloneEntityCountsToChartData(cyclone), period, today);

    return mergeCycloneIntoOpenMetadataOverview(openMetadataOverview, cycloneOverview);
  }, [cycloneEntityCountsQuery.data, openMetadataQuery.data, period]);

  return {
    overview,
    openMetadataChartData: openMetadataQuery.data,
    cycloneEntityCounts: cycloneEntityCountsQuery.data,
  };
};
